import { forwardRef, useEffect, useState } from "react"
import { Formik, FormikProps, useFormikContext } from "formik"
import { Currency } from "dinero.js"
import { DateTime } from "luxon"
import * as Yup from "yup"

import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardFormikErrorScrollWrapper } from "../../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"
import { numericFieldSchema } from "../../../../../../shared/components/forms/numericFieldValidation"
import { useMoney } from "../../../../../currency/useMoney"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"

import { AdjustmentUnit, DetailedPriceList, PriceAdjustmentType, RoundingMode } from "../../priceListTypes"
import { AdjustmentFields } from "./AdjustmentFields"
import { AdjustmentPanelSubtitle } from "./AdjustmentPanelSubtitle"
import { AdjustmentSummary } from "./AdjustmentSummary"
import {
    AdjustPricesFormValues,
    buildPreviewRows,
    countProductsInGroups,
    findExampleRow,
    getUnitLabel,
    hasNegativePrice,
    hasPriceChange,
    MAX_VALUE,
} from "./adjustPricesLib"
import { PreviewPanelSubtitle } from "./PreviewPanelSubtitle"
import { PreviewTable } from "./PreviewTable"
import { PreviewToggleLink } from "./PreviewToggleLink"
import { ScopeFields } from "./ScopeFields"
import { ScopeSelectAllLink } from "./ScopeSelectAllLink"
import { ScopeSubtitle } from "./ScopeSubtitle"

type AdjustPricesFormContentProps = Readonly<{
    priceList: DetailedPriceList
    currency: Currency<number>
    minDate: DateTime
    onSubmitBlockedChange: (isBlocked: boolean) => void
}>

function AdjustPricesFormContent({
    priceList,
    currency,
    minDate,
    onSubmitBlockedChange,
}: AdjustPricesFormContentProps) {
    const { values, submitCount, isValid } = useFormikContext<AdjustPricesFormValues>()
    const [isPreviewOpen, setIsPreviewOpen] = useState(false)

    // the submit button sits in the page header, outside Formik
    const isSubmitBlocked = submitCount > 0 && !isValid
    useEffect(() => {
        onSubmitBlockedChange(isSubmitBlocked)
    }, [isSubmitBlocked, onSubmitBlockedChange])

    const productGroups = priceList.productGroups ?? []
    const value = Number(values.value)
    const hasValue = values.value !== "" && Number.isFinite(value) && value > 0
    const hasScope = values.productGroupIds.length > 0

    const previewRows = hasValue
        ? buildPreviewRows({
              productGroups,
              selectedGroupIds: values.productGroupIds,
              settings: {
                  unit: values.unit,
                  value,
                  adjustmentType: values.adjustmentType,
                  rounding: values.rounding,
              },
          })
        : []

    const itemCount = countProductsInGroups(productGroups, values.productGroupIds)

    return (
        <StandardFormikErrorScrollWrapper>
            <StandardForm>
                <LocalizedStrict id="adjust-price-list-adjustment-panel" attrs={{ title: true }}>
                    <StandardMainPanel
                        title="Adjustment"
                        subtitle={<AdjustmentPanelSubtitle itemCount={itemCount} currency={currency} />}
                    >
                        <AdjustmentFields
                            priceListName={priceList.name}
                            currency={currency}
                            minDate={minDate}
                            example={findExampleRow(previewRows)}
                        />
                    </StandardMainPanel>
                </LocalizedStrict>

                <LocalizedStrict id="adjust-price-list-scope-panel" attrs={{ title: true }}>
                    <StandardMainPanel
                        title="Scope"
                        subtitle={<ScopeSubtitle productGroups={productGroups} />}
                        action={<ScopeSelectAllLink productGroups={productGroups} />}
                    >
                        <ScopeFields productGroups={productGroups} />
                    </StandardMainPanel>
                </LocalizedStrict>

                <LocalizedStrict id="adjust-price-list-preview-panel" attrs={{ title: true }}>
                    <StandardMainPanel
                        title="Preview"
                        subtitle={<PreviewPanelSubtitle rows={previewRows} hasScope={hasScope} />}
                        action={
                            previewRows.length > 0 && (
                                <PreviewToggleLink
                                    isOpen={isPreviewOpen}
                                    onToggle={() => setIsPreviewOpen(!isPreviewOpen)}
                                />
                            )
                        }
                    >
                        {isPreviewOpen && previewRows.length > 0 && <PreviewTable rows={previewRows} />}
                    </StandardMainPanel>
                </LocalizedStrict>

                <LocalizedStrict id="adjust-price-list-summary-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="What it adds up to">
                        <AdjustmentSummary values={values} itemCount={itemCount} currency={currency} />
                        <LocalizedStrict
                            id="adjust-price-list-customers-notice"
                            attrs={{ title: true, description: true }}
                            vars={{
                                shops: priceList.shops?.length ?? 0,
                                customers: priceList.customers?.length ?? 0,
                            }}
                        >
                            <StandardAlertInfo
                                severity="warning"
                                icon={false}
                                fullWidth
                                title="Customers see the change"
                                description="Prices are updated in the shops for the customers on this list."
                            />
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
            </StandardForm>
        </StandardFormikErrorScrollWrapper>
    )
}

type AdjustPricesFormProps = Readonly<{
    priceList: DetailedPriceList
    currency: Currency<number>
    onSubmit: (values: AdjustPricesFormValues) => Promise<void>
    /** True once the user has tried to submit and the form is still invalid. */
    onSubmitBlockedChange: (isBlocked: boolean) => void
}>

export const AdjustPricesForm = forwardRef<FormikProps<AdjustPricesFormValues>, AdjustPricesFormProps>(
    function AdjustPricesForm({ priceList, currency, onSubmit, onSubmitBlockedChange }, ref) {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()
        const moneyFactory = useMoney({})

        const today = dateTimeFactory.now().startOf("day")

        const validationSchema = Yup.object({
            value: numericFieldSchema("positive-decimal", l10n, l10n.getString("adjust-price-list-value-field-name"))
                .required(l10n.getString("required-field"))
                .test("max-value", (value, context) => {
                    if (value === undefined || value === null || value <= MAX_VALUE) return true

                    return context.createError({
                        message: l10n.getString("adjust-price-list-value-max-value", {
                            max: String(MAX_VALUE),
                            unit: getUnitLabel(moneyFactory, context.parent.unit, currency),
                        }),
                    })
                }),
            effectiveFrom: Yup.mixed()
                .required(l10n.getString("required-field"))
                .test(
                    "is-valid-future-date",
                    l10n.getString("adjust-price-list-effective-from-invalid"),
                    (value) => DateTime.isDateTime(value) && value.isValid && value.startOf("day") >= today,
                ),
            productGroupIds: Yup.array().min(1, l10n.getString("adjust-price-list-scope-required")),
        })

        const validate = ({ unit, value, adjustmentType, rounding, productGroupIds }: AdjustPricesFormValues) => {
            // the schema reports out-of-range values and an empty scope; do not talk over it
            if (value === "" || !(value > 0 && value <= MAX_VALUE) || productGroupIds.length === 0) return {}

            const rows = buildPreviewRows({
                productGroups: priceList.productGroups ?? [],
                selectedGroupIds: productGroupIds,
                settings: { unit, value, adjustmentType, rounding },
            })

            if (hasNegativePrice(rows)) return { value: l10n.getString("adjust-price-list-value-negative-price") }
            if (!rows.some(hasPriceChange)) return { value: l10n.getString("adjust-price-list-value-no-change") }

            return {}
        }

        const initialValues: AdjustPricesFormValues = {
            adjustmentType: PriceAdjustmentType.INCREASE,
            unit: AdjustmentUnit.PERCENTAGE,
            value: "",
            rounding: RoundingMode.NONE,
            effectiveFrom: null,
            updateExistingOrders: false,
            productGroupIds: (priceList.productGroups ?? []).map((group) => group.id),
        }

        return (
            <Formik<AdjustPricesFormValues>
                innerRef={ref}
                initialValues={initialValues}
                validationSchema={validationSchema}
                validate={validate}
                validateOnMount
                onSubmit={onSubmit}
            >
                <AdjustPricesFormContent
                    priceList={priceList}
                    currency={currency}
                    minDate={today}
                    onSubmitBlockedChange={onSubmitBlockedChange}
                />
            </Formik>
        )
    },
)
